import React, { useEffect, useState } from "react";
import { Polyline } from "react-leaflet";
import axios from "axios";
import { Spin } from "antd";

const getFastestRoute = async (stopCoordinates) => {
  if (!Array.isArray(stopCoordinates) || stopCoordinates.length < 2) {
    return stopCoordinates || [];
  }

  try {
    const routeCoordinates = [...stopCoordinates];

    // -------------------------------------------------------
    // Round-trip support
    // -------------------------------------------------------

    const firstPoint = routeCoordinates[0];
    const lastPoint = routeCoordinates[routeCoordinates.length - 1];

    const sameStartAndEnd =
      firstPoint &&
      lastPoint &&
      firstPoint[0] === lastPoint[0] &&
      firstPoint[1] === lastPoint[1];

    // If this is a round trip and the first station isn't already
    // the final point, append it so OSRM calculates the final leg.
    if (!sameStartAndEnd) {
      routeCoordinates.push([firstPoint[0], firstPoint[1]]);
    }

    const validCoordinates = routeCoordinates.filter(
      (point) =>
        Array.isArray(point) &&
        point.length >= 2 &&
        Number.isFinite(Number(point[0])) &&
        Number.isFinite(Number(point[1])),
    );

    if (validCoordinates.length < 2) {
      return stopCoordinates;
    }

    const coordinates = validCoordinates
      .map(([lat, lng]) => `${lng},${lat}`)
      .join(";");

    const response = await axios.get(
      `https://router.project-osrm.org/route/v1/driving/${coordinates}`,
      {
        params: {
          overview: "full",
          geometries: "geojson",
          steps: false,
        },
      },
    );

    const route = response?.data?.routes?.[0]?.geometry?.coordinates;

    if (!Array.isArray(route) || route.length < 2) {
      return stopCoordinates;
    }

    return route.map(([lng, lat]) => [lat, lng]);
  } catch (error) {
    console.error("Failed to create planned route:", error);

    return stopCoordinates;
  }
};

const UpcomingTripMapComponents = ({
  stopCoordinates = [],
  fastestRouteCoordinates = [],
}) => {
  const [plannedRoute, setPlannedRoute] = useState([]);
  const [routeLoading, setRouteLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadPlannedRoute = async () => {
      setRouteLoading(true);

      // -------------------------------------------------------
      // 1. USE ROUTE FROM DB
      // -------------------------------------------------------

      const hasStoredRoute =
        Array.isArray(fastestRouteCoordinates) &&
        fastestRouteCoordinates.length >= 2;

      if (hasStoredRoute) {
        if (!cancelled) {
          setPlannedRoute(fastestRouteCoordinates);
          setRouteLoading(false);
        }

        return;
      }

      // -------------------------------------------------------
      // 2. FALLBACK: CREATE ROUTE USING OSRM
      // -------------------------------------------------------

      try {
        const generatedRoute = await getFastestRoute(stopCoordinates);

        if (cancelled) return;

        setPlannedRoute(generatedRoute || []);
      } catch (error) {
        console.error("Failed to generate upcoming planned route:", error);

        if (!cancelled) {
          setPlannedRoute(stopCoordinates || []);
        }
      } finally {
        if (!cancelled) {
          setRouteLoading(false);
        }
      }
    };

    loadPlannedRoute();

    return () => {
      cancelled = true;
    };
  }, [stopCoordinates, fastestRouteCoordinates]);

  // -------------------------------------------------------
  // Loading
  // -------------------------------------------------------

  if (routeLoading) {
    return (
      <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-white/40">
        <Spin size="large" />
      </div>
    );
  }

  // -------------------------------------------------------
  // Nothing to draw
  // -------------------------------------------------------

  if (!Array.isArray(plannedRoute) || plannedRoute.length < 2) {
    return null;
  }

  const routeKey = plannedRoute.map(([lat, lng]) => `${lat},${lng}`).join("|");

  return (
    <Polyline
      key={`upcoming-planned-${routeKey}`}
      positions={plannedRoute}
      pathOptions={{
        color: "#525252",
        weight: 6,
        lineCap: "round",
        lineJoin: "round",
      }}
    />
  );
};

export default UpcomingTripMapComponents;
